export interface AdminOrganizationListItem {
  id: string
  name: string
  tenant_id: string
  type: 'standard' | 'partner'
  workflow_count: number
}

export interface AdminOrganizationDetail {
  id: string
  name: string
  tenant_id: string
  root_organization: boolean
  type: 'standard' | 'partner'
}

export const WORKFLOW_TYPES = [
  { value: 'oc', label: 'Order confirmation' },
  { value: 'so', label: 'Sales order' },
] as const

export type WorkflowType = (typeof WORKFLOW_TYPES)[number]['value']
// Who a run's output is for. Not the deployment stage.
export type WorkflowAudience = 'test' | 'customer'

export const WORKFLOW_AUDIENCES = [
  { value: 'test', label: 'Test' },
  { value: 'customer', label: 'Customer' },
] as const

export interface AdminWorkflow {
  id: string
  name: string
  type: string
  audience: WorkflowAudience
  status: WorkflowStatus
  start_mode: WorkflowStartMode
}

// The state of a mailbox connection, as the API computes it from the two
// timestamps. Not derived here, so every client agrees.
export type MailboxConnectionState = 'unconfigured' | 'untested' | 'failing' | 'ok'

export interface AdminWorkflowEmailSettings {
  address: string
  graph_tenant_id: string
  graph_client_id: string
  use_default_inbox: boolean
  inbox: string
  in_progress: string
  handled: string
  not_handled: string
  last_connected_at: string | null
  last_connection_attempt_at: string | null
  connection_state: MailboxConnectionState
}

export const ERP_PROVIDERS = [
  { value: 'monitor', label: 'Monitor' },
  { value: 'jeeves', label: 'Jeeves' },
  { value: 'businesscentral', label: 'Business Central' },
  { value: 'd365fo', label: 'Finance and Operations' },
] as const

export type ErpProvider = (typeof ERP_PROVIDERS)[number]['value']

/** One provider's credentials, split the way the API is willing to show them:
 *  real values for the safe fields, and only whether each secret field is
 *  set — never its value. */
export interface ErpCredentialsFields {
  safe: Record<string, string | boolean>
  secret_fields: Record<string, boolean>
}

export interface AdminWorkflowErpSettings {
  provider: string
  last_connected_at: string | null
  last_connection_attempt_at: string | null
  credentials?: ErpCredentialsFields
}

export type ErpCredentialField =
  | { key: string; label: string; type: 'text' }
  | { key: string; label: string; type: 'checkbox' }

/** Mirrors api/admin/erpcredentials.go's viewErpCredentials/mergeErpCredentials
 *  field-by-field — keep the two in sync by hand; nothing enforces it. */
export const ERP_CREDENTIAL_FIELDS: Record<
  ErpProvider,
  { safe: ErpCredentialField[]; secret: { key: string; label: string }[] }
> = {
  monitor: {
    safe: [
      { key: 'host', label: 'Host', type: 'text' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'language_code', label: 'Language code', type: 'text' },
      { key: 'company', label: 'Company', type: 'text' },
      { key: 'validate_certificate', label: 'Validate certificate', type: 'checkbox' },
    ],
    secret: [{ key: 'password', label: 'Password' }],
  },
  jeeves: {
    safe: [{ key: 'url', label: 'URL', type: 'text' }],
    secret: [{ key: 'api_key', label: 'API key' }],
  },
  businesscentral: {
    safe: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text' },
      { key: 'environment', label: 'Environment', type: 'text' },
      { key: 'company', label: 'Company', type: 'text' },
      { key: 'client_id', label: 'Client ID', type: 'text' },
    ],
    secret: [{ key: 'client_secret', label: 'Client secret' }],
  },
  /** Not Business Central's shape despite both being Entra apps: F&O bakes the
   *  region into the hostname, so the whole environment URL is stored rather
   *  than a name, and the legal entity is the data area id. */
  d365fo: {
    safe: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text' },
      { key: 'environment_url', label: 'Environment URL', type: 'text' },
      { key: 'data_area_id', label: 'Data area ID', type: 'text' },
      { key: 'client_id', label: 'Client ID', type: 'text' },
    ],
    secret: [{ key: 'client_secret', label: 'Client secret' }],
  },
}

export interface WorkflowFlowTransition {
  from: string
  to: string
  side_effects?: string[]
}

export interface WorkflowFlowFallback {
  on: string
  triggers: string[]
}

// Mirrors workflow.Flow. Null when the workflow has none yet.
export interface WorkflowFlow {
  start: string
  end: { block: string; side_effects?: string[] }
  transitions: WorkflowFlowTransition[]
  fallbacks: WorkflowFlowFallback[]
}

/** A block's own settings for one workflow. Settings is whatever shape that
 *  block is registered with — opaque here, edited as JSON. */
export interface AdminWorkflowBlockSettings {
  block_name: string
  settings: unknown
}

/** Rolled up from the mailbox, flow and ERP by the API. Not "status": that is draft/active/paused. */
export type WorkflowReadiness = 'ok' | 'warning' | 'error' | 'unconfigured'

export type WorkflowStatus = 'draft' | 'active' | 'paused' | 'archived'
export type WorkflowStartMode = 'scheduled' | 'manual'

export interface AdminWorkflowListItem {
  id: string
  name: string
  type: string
  audience: WorkflowAudience
  status: WorkflowStatus
  start_mode: WorkflowStartMode
  organization_id: string
  organization_name: string
  mailbox_state: MailboxConnectionState
  flow_configured: boolean
  erp_provider: string
  readiness: WorkflowReadiness
}
